import { createSignal, onMount, For, Show, createMemo } from 'solid-js';
import { EmptyState } from '../components/EmptyState';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import BotStatusBadge from '../components/BotStatusBadge';
import {
    type VoteSiteDto,
    type VoteStateDto,
    voteOutcomeKey,
    isPositiveOutcome,
} from '../services/votingApi';
import { GrantTypes } from '../services/grantsApi';
import { formatDateTime } from '../services/dateFormat';
import { refreshLicenses } from '../hooks/useLicense';
import styles from './Voting.module.css';
import { firstError } from '../api/envelope';

const POLL_MS = 10_000;

interface BotRow {
    id: string;
    minecraftUsername: string;
    isOwner: boolean;
    state: string;
    votingEnabled: boolean;
}

interface AdHocSiteStatus {
    siteKey: string;
    siteDisplayName: string;
    lastVoteUtc: string | null;
    lastOutcome: number;
    isStale: boolean;
}

interface AdHocTarget {
    minecraftUsername: string;
    sites: AdHocSiteStatus[];
}

type SiteStatusKind = 'success' | 'pending' | 'failed' | 'stale';
interface UnifiedSiteStatus {
    siteKey: string;
    siteDisplayName: string;
    kind: SiteStatusKind;
    lastVoteUtc: string | null;
    lastOutcome: number;
    redeemed?: boolean;
}

export default function Voting() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [bots, setBots] = createSignal<BotRow[]>([]);
    const [sites, setSites] = createSignal<VoteSiteDto[]>([]);
    const [states, setStates] = createSignal<VoteStateDto[]>([]);
    const [adHocs, setAdHocs] = createSignal<AdHocTarget[]>([]);
    const [quotaUsed, setQuotaUsed] = createSignal(0);
    const [quotaTotal, setQuotaTotal] = createSignal(0);
    const [loading, setLoading] = createSignal(true);
    const [busyId, setBusyId] = createSignal<string | null>(null);
    const [adHocOpen, setAdHocOpen] = createSignal(false);
    const [adHocUsername, setAdHocUsername] = createSignal('');
    const [adHocBusy, setAdHocBusy] = createSignal(false);
    const [available, setAvailable] = createSignal(true);

    const fetchAll = async () => {
        const [botsRes, sitesRes, statesRes, adhocRes, grantsRes, availRes] = await Promise.all([
            api.botRegistry.listBots(),
            api.voting.getSites(),
            api.voting.getState(),
            api.voting.myAdHoc(),
            api.grants.me(),
            api.voting.availability(),
        ]);
        if (isOk(botsRes.data)) {
            const v = botsRes.data.value as unknown as { bots?: BotRow[] } | BotRow[] | undefined;
            setBots((Array.isArray(v) ? v : (v?.bots ?? [])) as BotRow[]);
        }
        if (isOk(sitesRes.data)) {
            const v = sitesRes.data.value as unknown as { sites?: VoteSiteDto[] } | VoteSiteDto[] | undefined;
            setSites((Array.isArray(v) ? v : (v?.sites ?? [])) as VoteSiteDto[]);
        }
        if (isOk(statesRes.data)) {
            const v = statesRes.data.value as unknown as { states?: VoteStateDto[] } | VoteStateDto[] | undefined;
            setStates((Array.isArray(v) ? v : (v?.states ?? [])) as VoteStateDto[]);
        }
        if (isOk(adhocRes.data)) {
            const v = adhocRes.data.value as unknown as { targets?: AdHocTarget[] } | AdHocTarget[] | undefined;
            setAdHocs((Array.isArray(v) ? v : (v?.targets ?? [])) as AdHocTarget[]);
        }
        if (isOk(availRes.data)) setAvailable((availRes.data.value as { available?: boolean } | undefined)?.available !== false);
        if (isOk(grantsRes.data)) {
            const entries = (grantsRes.data.value as { entries?: { type: string; effectiveValue: number; used: number }[] } | undefined)?.entries ?? [];
            const slot = entries.find(e => e.type === GrantTypes.VoteSlot);
            setQuotaUsed(slot?.used ?? 0);
            setQuotaTotal(slot?.effectiveValue ?? 0);
        }
        if (loading()) setLoading(false);
    };

    onMount(() => {
        void fetchAll();
        useVisibilityPoll(() => void fetchAll(), POLL_MS);
    });

    const ownedBots = createMemo(() => bots().filter(b => b.isOwner));
    const enabledSites = createMemo(() => sites().filter(s => s.isEnabled));
    const statesByBot = createMemo(() => {
        const map = new Map<string, VoteStateDto[]>();
        for (const s of states()) {
            const arr = map.get(s.botRegistryId) ?? [];
            arr.push(s);
            map.set(s.botRegistryId, arr);
        }
        return map;
    });

    const utcMidnight = () => Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());

    const classifyStateRow = (s: VoteStateDto | undefined): SiteStatusKind => {
        if (!s) return 'pending';
        if (s.isStale) return 'stale';
        if (!s.lastVoteUtc) return 'pending';
        const dayStart = utcMidnight();
        if (new Date(s.lastVoteUtc).getTime() < dayStart) return 'pending';
        return isPositiveOutcome(s.lastOutcome) ? 'success' : 'failed';
    };

    const classifyAdHocRow = (s: AdHocSiteStatus): SiteStatusKind => {
        if (s.isStale) return 'stale';
        if (!s.lastVoteUtc) return 'pending';
        const dayStart = utcMidnight();
        if (new Date(s.lastVoteUtc).getTime() < dayStart) return 'pending';
        return isPositiveOutcome(s.lastOutcome) ? 'success' : 'failed';
    };

    const botSiteRows = (botId: string): UnifiedSiteStatus[] => {
        const stateMap = new Map<string, VoteStateDto>();
        for (const s of statesByBot().get(botId) ?? []) stateMap.set(s.siteKey, s);
        return enabledSites().map<UnifiedSiteStatus>(site => {
            const s = stateMap.get(site.key);
            const kind = classifyStateRow(s);
            return {
                siteKey: site.key,
                siteDisplayName: site.displayName,
                kind,
                lastVoteUtc: s?.lastVoteUtc ?? null,
                lastOutcome: s?.lastOutcome ?? 9,
            };
        });
    };

    /** Bot-level redemption: all of today's successful votes have been claimed via /geschenk. */
    type RedemptionState = 'allDone' | 'pending' | 'nothingToRedeem';
    const botRedemptionState = (botId: string): RedemptionState => {
        const list = statesByBot().get(botId) ?? [];
        const dayStart = utcMidnight();
        let successesToday = 0;
        let redeemed = 0;
        for (const s of list) {
            if (!s.lastVoteUtc) continue;
            if (new Date(s.lastVoteUtc).getTime() < dayStart) continue;
            if (!isPositiveOutcome(s.lastOutcome)) continue;
            successesToday++;
            if (s.lastRedeemedUtc && new Date(s.lastRedeemedUtc).getTime() >= new Date(s.lastVoteUtc).getTime()) redeemed++;
        }
        if (successesToday === 0) return 'nothingToRedeem';
        return redeemed >= successesToday ? 'allDone' : 'pending';
    };

    const adHocSiteRows = (target: AdHocTarget): UnifiedSiteStatus[] => {
        return target.sites.map<UnifiedSiteStatus>(s => ({
            siteKey: s.siteKey,
            siteDisplayName: s.siteDisplayName,
            kind: classifyAdHocRow(s),
            lastVoteUtc: s.lastVoteUtc,
            lastOutcome: s.lastOutcome,
        }));
    };

    const summarize = (rows: UnifiedSiteStatus[]) => {
        let succeeded = 0, pending = 0, failed = 0, stale = 0;
        for (const r of rows) {
            if (r.kind === 'success') succeeded++;
            else if (r.kind === 'pending') pending++;
            else if (r.kind === 'failed') failed++;
            else if (r.kind === 'stale') stale++;
        }
        return { succeeded, pending, failed, stale, total: rows.length };
    };

    const totalToday = createMemo(() => {
        let succeeded = 0, total = 0;
        for (const b of ownedBots()) {
            if (!b.votingEnabled) continue;
            const r = summarize(botSiteRows(b.id));
            succeeded += r.succeeded;
            total += r.total;
        }
        for (const a of adHocs()) {
            const r = summarize(adHocSiteRows(a));
            succeeded += r.succeeded;
            total += r.total;
        }
        return { succeeded, total };
    });

    const setVoting = async (bot: BotRow, enable: boolean) => {
        setBusyId(bot.id);
        const res = await api.botRegistry.setVoting(bot.id, { enabled: enable });
        setBusyId(null);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('VOTING_TOGGLE_FAILED')); return; }
        toast?.info(enable ? t('VOTING_ENABLED') : t('VOTING_DISABLED'));
        refreshLicenses();
        void fetchAll();
    };

    const removeAdHoc = async (target: AdHocTarget) => {
        const ok = await showConfirm(t('VOTING_ADHOC_REMOVE_CONFIRM', target.minecraftUsername), true);
        if (!ok) return;
        setBusyId(target.minecraftUsername);
        const res = await api.voting.deleteMyAdHocByUsername(target.minecraftUsername);
        setBusyId(null);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('VOTING_ADHOC_REMOVE_FAILED')); return; }
        toast?.info(t('VOTING_ADHOC_REMOVED'));
        refreshLicenses();
        void fetchAll();
    };

    const submitAdHoc = async () => {
        const username = adHocUsername().trim();
        if (!username) { toast?.error(t('VOTING_ADHOC_ENTER_USERNAME')); return; }
        setAdHocBusy(true);
        const res = await api.voting.createMyAdHoc({ minecraftUsername: username });
        setAdHocBusy(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('VOTING_ADHOC_ADD_FAILED')); return; }
        toast?.info(t('VOTING_ADHOC_ADDED'));
        setAdHocUsername('');
        setAdHocOpen(false);
        refreshLicenses();
        void fetchAll();
    };

    const StatusDot = (props: { kind: SiteStatusKind }) => {
        const colorMap: Record<SiteStatusKind, string> = {
            success: 'var(--success-500)',
            pending: 'var(--dark-text-muted)',
            failed: 'var(--error-500)',
            stale: 'var(--warning-500)',
        };
        const iconMap: Record<SiteStatusKind, string> = {
            success: 'fa-check',
            pending: 'fa-clock',
            failed: 'fa-xmark',
            stale: 'fa-pause',
        };
        return (
            <span style={{
                display: 'inline-flex',
                'align-items': 'center',
                'justify-content': 'center',
                width: '1.5rem', height: '1.5rem',
                'border-radius': '50%',
                background: `color-mix(in srgb, ${colorMap[props.kind]} 18%, transparent)`,
                color: colorMap[props.kind],
                'font-size': 'var(--font-size-xs)',
            }}>
                <i class={`fa-solid ${iconMap[props.kind]}`} />
            </span>
        );
    };

    const SiteRow = (props: { row: UnifiedSiteStatus }) => (
        <div class="h" style={{
            'align-items': 'center',
            gap: 'var(--spacing-md)',
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            'border-radius': 'var(--radius-md)',
        }}>
            <StatusDot kind={props.row.kind} />
            <div class="v" style={{ flex: 1, gap: '2px', 'min-width': 0 }}>
                <span style={{ 'font-weight': 500, 'font-size': 'var(--font-size-sm)' }}>{props.row.siteDisplayName}</span>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}>
                    <Show when={props.row.lastVoteUtc} fallback={
                        <Show when={props.row.kind === 'stale'} fallback={<Text val="VOTING_NEVER_VOTED" />}>
                            <Text val="VOTING_STALE_HINT" />
                        </Show>
                    }>
                        {formatDateTime(props.row.lastVoteUtc!)} · <Text val={voteOutcomeKey(props.row.lastOutcome)} />
                    </Show>
                </span>
            </div>
        </div>
    );

    const RedemptionBadge = (props: { state: RedemptionState }) => (
        <Show when={props.state === 'allDone'}>
            <span class={styles.outcomeBadge} style={{ background: 'color-mix(in srgb, var(--success-500) 18%, transparent)', color: 'var(--success-500)', display: 'inline-flex', 'align-items': 'center', gap: '4px' }}>
                <i class="fa-solid fa-gift" /> <Text val="VOTING_REDEEMED" />
            </span>
        </Show>
    );

    const RedemptionPending = (props: { state: RedemptionState }) => (
        <Show when={props.state === 'pending'}>
            <span class={styles.outcomeBadge} style={{ background: 'color-mix(in srgb, var(--warning-500) 18%, transparent)', color: 'var(--warning-500)', display: 'inline-flex', 'align-items': 'center', gap: '4px' }}>
                <i class="fa-solid fa-clock" /> <Text val="VOTING_REDEEM_PENDING" />
            </span>
        </Show>
    );

    const SummaryChips = (props: { rows: UnifiedSiteStatus[] }) => {
        const r = summarize(props.rows);
        return (
            <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap', 'font-size': 'var(--font-size-sm)' }}>
                <span style={{ color: 'var(--success-500)', 'font-weight': 600 }}>{r.succeeded} / {r.total}</span>
                <Show when={r.pending > 0}>
                    <span style={{ color: 'var(--dark-text-muted)' }}>· {r.pending} <Text val="VOTING_PENDING" /></span>
                </Show>
                <Show when={r.failed > 0}>
                    <span style={{ color: 'var(--error-500)' }}>· {r.failed} <Text val="VOTING_FAILED" /></span>
                </Show>
                <Show when={r.stale > 0}>
                    <span style={{ color: 'var(--warning-500)' }}>· {r.stale} <Text val="VOTING_STALE" /></span>
                </Show>
            </div>
        );
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="VOTING_TITLE" /></h1>
                <span class={styles.entryCount}>
                    <i class="fa-solid fa-square-poll-vertical" /> {totalToday().succeeded} / {totalToday().total} <Text val="VOTING_TODAY_LABEL" />
                </span>
            </div>

            <p style={{ color: 'var(--dark-text-muted)', margin: 0, 'font-size': 'var(--font-size-sm)' }}>
                <Text val="VOTING_HELP" />
            </p>

            <Show when={!available()}>
                <div class="fcard" style={{ 'margin-top': 'var(--spacing-md)', 'border-left': '4px solid var(--warning-500)' }}>
                    <div class="h" style={{ gap: 'var(--spacing-md)', 'align-items': 'center' }}>
                        <i class="fa-solid fa-triangle-exclamation" style={{ color: 'var(--warning-500)', 'font-size': 'var(--font-size-xl)' }} />
                        <span><Text val="VOTING_UNAVAILABLE" /></span>
                    </div>
                </div>
            </Show>

            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'margin-top': 'var(--spacing-md)', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                <span style={{ color: 'var(--dark-text-muted)' }}>
                    <i class="fa-solid fa-id-badge" style={{ 'margin-right': '6px' }} />
                    <Text val="VOTING_QUOTA" />: <span style={{ 'font-weight': 600, color: 'var(--dark-text-primary)' }}>{quotaUsed()} / {quotaTotal()}</span>
                </span>
                <button class="btn btn-secondary btn-sm" onClick={() => setAdHocOpen(v => !v)}>
                    <i class="fa-solid fa-plus" /> <Text val="VOTING_ADD_ADHOC" />
                </button>
            </div>

            <Show when={adHocOpen()}>
                <form class="fcard v" style={{ gap: 'var(--spacing-md)', 'margin-top': 'var(--spacing-md)' }}
                    onSubmit={(e) => { e.preventDefault(); void submitAdHoc(); }}>
                    <p style={{ margin: 0, color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                        <Text val="VOTING_ADHOC_HELP" />
                    </p>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="VOTING_ADHOC_USERNAME" />
                        </span>
                        <input type="text" placeholder="Aktenkoffer_" value={adHocUsername()} onInput={(e) => setAdHocUsername(e.currentTarget.value)} />
                    </label>
                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-secondary" onClick={() => setAdHocOpen(false)}><Text val="CANCEL" /></button>
                        <button type="submit" class="btn btn-primary" disabled={adHocBusy() || !adHocUsername().trim()}>
                            <Show when={adHocBusy()} fallback={<Text val="VOTING_ADD_ADHOC" />}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                    </div>
                </form>
            </Show>

            <Show when={!loading()} fallback={
                <div class={styles.list} style={{ 'margin-top': 'var(--spacing-lg)' }}>
                    <div class={styles.skeleton} />
                </div>
            }>
                <Show when={ownedBots().length > 0 || adHocs().length > 0} fallback={
                    <EmptyState icon="fa-square-poll-vertical" title={t('VOTING_NOTHING')} />
                }>
                    <div class={styles.list} style={{ 'margin-top': 'var(--spacing-lg)' }}>
                        <For each={ownedBots()}>{(bot) => {
                            const rows = createMemo(() => botSiteRows(bot.id));
                            const redemption = createMemo(() => botRedemptionState(bot.id));
                            return (
                                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                    <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                                        <i class="fa-solid fa-robot" style={{ color: 'var(--primary-400)', 'font-size': 'var(--font-size-lg)' }} />
                                        <span style={{ 'font-weight': 600, 'font-size': 'var(--font-size-lg)' }}>{bot.minecraftUsername}</span>
                                        <BotStatusBadge state={bot.state as any} />
                                        <Show when={bot.votingEnabled}>
                                            <RedemptionBadge state={redemption()} />
                                            <RedemptionPending state={redemption()} />
                                        </Show>
                                        <div style={{ 'margin-left': 'auto' }}>
                                            <button
                                                class={`btn btn-sm ${bot.votingEnabled ? 'btn-secondary' : 'btn-primary'}`}
                                                disabled={busyId() === bot.id}
                                                onClick={() => setVoting(bot, !bot.votingEnabled)}>
                                                <Show when={busyId() === bot.id} fallback={
                                                    <Text val={bot.votingEnabled ? 'VOTING_TURN_OFF' : 'VOTING_TURN_ON'} />
                                                }>
                                                    <i class="fa-solid fa-spinner fa-spin" />
                                                </Show>
                                            </button>
                                        </div>
                                    </div>

                                    <Show when={bot.votingEnabled} fallback={
                                        <div style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                                            <Text val="VOTING_OFF_HINT" />
                                        </div>
                                    }>
                                        <SummaryChips rows={rows()} />
                                        <div class="v" style={{ gap: '2px', 'border-top': '1px solid var(--dark-border)', 'padding-top': 'var(--spacing-sm)' }}>
                                            <For each={rows()}>{(r) => <SiteRow row={r} />}</For>
                                        </div>
                                    </Show>
                                </div>
                            );
                        }}</For>

                        <For each={adHocs()}>{(target) => {
                            const rows = createMemo(() => adHocSiteRows(target));
                            return (
                                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                    <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                                        <i class="fa-solid fa-user-tag" style={{ color: 'var(--accent-purple)', 'font-size': 'var(--font-size-lg)' }} />
                                        <span style={{ 'font-weight': 600, 'font-size': 'var(--font-size-lg)' }}>{target.minecraftUsername}</span>
                                        <span class={styles.outcomeBadge} style={{ background: 'color-mix(in srgb, var(--accent-purple) 18%, transparent)', color: 'var(--accent-purple)' }}>
                                            <Text val="VOTING_ADHOC_BADGE" />
                                        </span>
                                        <div style={{ 'margin-left': 'auto' }}>
                                            <button class="btn btn-danger btn-sm" disabled={busyId() === target.minecraftUsername} onClick={() => removeAdHoc(target)}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                    <SummaryChips rows={rows()} />
                                    <div class="v" style={{ gap: '2px', 'border-top': '1px solid var(--dark-border)', 'padding-top': 'var(--spacing-sm)' }}>
                                        <For each={rows()}>{(r) => <SiteRow row={r} />}</For>
                                    </div>
                                </div>
                            );
                        }}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
